import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "approvalComments",
    "rejectionComments",
    "approveBtn",
    "rejectBtn",
    "deleteBtn",
    "competencyComment",
  ];

  connect() {
    console.log("Admin approval controller connected");
  }

  approveAllCompetencies(event) {
    console.log("=== Approve all competencies method called ===");
    event.preventDefault();
    event.stopPropagation();

    const btn = event.currentTarget;
    const url = btn.dataset.url;
    const pendingCount = parseInt(btn.dataset.pendingCount);

    btn.disabled = true;
    const originalText = btn.innerHTML;
    btn.innerHTML =
      '<i class="glyphicon glyphicon-refresh spinning"></i> Approving...';

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: JSON.stringify({}),
    })
      .then((response) => {
        console.log("Response received:", response);
        return response.json();
      })
      .then((data) => {
        console.log("Data:", data);
        if (data.success) {
          // Update all pending rows to show they're approved
          const table = btn.closest("table");
          const rows = table.querySelectorAll("tr.info");

          rows.forEach((row) => {
            row.classList.remove("info");
            row.classList.add("success");
            const statusCell = row.querySelector("td:nth-child(5)");
            if (statusCell) {
              statusCell.innerHTML =
                '<span class="label label-success"><i class="glyphicon glyphicon-ok"></i> Approved</span>';
            }
            const actionsCell = row.querySelector("td:nth-child(6)");
            if (actionsCell) {
              actionsCell.innerHTML =
                '<span class="text-muted">Approved</span>';
            }
          });

          // Hide the "Approve All" button
          btn.style.display = "none";

          // Update the pending count on the approve rating button
          this.updatePendingCount();

          // Show a success notification
          this.showBriefAlert(
            "success",
            `<i class="glyphicon glyphicon-ok"></i> ${data.message}`
          );
        } else {
          this.showAlert(
            "danger",
            data.error || data.message || "Error approving competencies"
          );
          btn.disabled = false;
          btn.innerHTML = originalText;
        }
      })
      .catch((error) => {
        console.log("Error:", error);
        this.showAlert("danger", "Error: " + error.message);
        btn.disabled = false;
        btn.innerHTML = originalText;
      });
  }

  approveCompetency(event) {
    console.log("=== Approve competency method called ===");
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const btn = event.currentTarget;
    const url = btn.dataset.url;
    const row = btn.closest("tr");

    btn.disabled = true;
    const originalText = btn.innerHTML;
    btn.innerHTML = '<i class="glyphicon glyphicon-refresh spinning"></i>';

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: JSON.stringify({ comments: "" }),
    })
      .then((response) => {
        console.log("Response received:", response);
        return response.json();
      })
      .then((data) => {
        console.log("Data:", data);
        if (data.success) {
          // Update the row to show it's approved
          row.classList.remove("info");
          row.classList.add("success");
          const statusCell = row.querySelector("td:nth-child(5)"); // Changed from 4 to 5 (added Candidate column)
          statusCell.innerHTML =
            '<span class="label label-success"><i class="glyphicon glyphicon-ok"></i> Approved</span>';
          const actionsCell = row.querySelector("td:nth-child(6)"); // Changed from 5 to 6
          actionsCell.innerHTML = '<span class="text-muted">Approved</span>';

          // Update the pending count on the approve rating button
          this.updatePendingCount();

          // Show a brief success message at the top
          this.showBriefAlert(
            "success",
            '<i class="glyphicon glyphicon-ok"></i> Competency approved'
          );
        } else {
          this.showAlert(
            "danger",
            data.error || data.message || "Error approving competency"
          );
          btn.disabled = false;
          btn.innerHTML = originalText;
        }
      })
      .catch((error) => {
        console.log("Error:", error);
        this.showAlert("danger", "Error: " + error.message);
        btn.disabled = false;
        btn.innerHTML = originalText;
      });

    return false;
  }

  rejectCompetency(event) {
    console.log("Reject competency method called");
    event.preventDefault();
    event.stopPropagation();

    const btn = event.currentTarget;
    const url = btn.dataset.url;
    const commentFieldId = btn.dataset.commentFieldId;
    const commentField = document.getElementById(commentFieldId);
    const comments = commentField.value.trim();
    const row = btn.closest("tr");

    if (!comments) {
      commentField.style.border = "2px solid red";
      commentField.focus();
      this.showBriefAlert("warning", "Rejection reason is required");
      return;
    }

    btn.disabled = true;
    const originalText = btn.innerHTML;
    btn.innerHTML = '<i class="glyphicon glyphicon-refresh spinning"></i>';

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: JSON.stringify({ comments: comments }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          // Update the row to show it's rejected
          row.classList.remove("info");
          row.classList.add("danger");
          const statusCell = row.querySelector("td:nth-child(5)"); // Changed from 4 to 5
          statusCell.innerHTML =
            '<span class="label label-danger"><i class="glyphicon glyphicon-remove"></i> Rejected</span>';
          const actionsCell = row.querySelector("td:nth-child(6)"); // Changed from 5 to 6
          actionsCell.innerHTML = `<small class="text-muted"><strong>Reason:</strong> ${comments}</small>`;

          // Update the pending count on the approve rating button
          this.updatePendingCount();

          this.showBriefAlert(
            "warning",
            '<i class="glyphicon glyphicon-remove"></i> Competency rejected'
          );
        } else {
          this.showAlert(
            "danger",
            data.error || data.message || "Error rejecting competency"
          );
          btn.disabled = false;
          btn.innerHTML = originalText;
        }
      })
      .catch((error) => {
        this.showAlert("danger", "Error: " + error.message);
        btn.disabled = false;
        btn.innerHTML = originalText;
      });
  }

  approve(event) {
    console.log("Approve rating method called", event);
    event.preventDefault();
    event.stopPropagation();

    const btn = event.currentTarget;
    const pendingCount = parseInt(btn.dataset.pendingCount);
    const comments = this.approvalCommentsTarget.value;

    // Only show confirmation if there are pending competencies
    if (pendingCount > 0) {
      let message = `There are ${pendingCount} competency checks still pending approval. Are you sure you want to approve the overall rating?`;
      if (!confirm(message)) return;
    }

    btn.disabled = true;
    btn.innerHTML =
      '<i class="glyphicon glyphicon-refresh spinning"></i> Approving...';

    console.log("Submitting to:", btn.dataset.url);

    fetch(btn.dataset.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: JSON.stringify({ comments: comments }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.showBriefAlert(
            "success",
            '<i class="glyphicon glyphicon-ok"></i> ' + data.message
          );
          // Redirect to the approvals list instead of reloading the current page
          setTimeout(
            () =>
              (window.location.href =
                btn.dataset.redirectUrl || "/admin/student_rater_approvals"),
            2000
          );
        } else {
          this.showAlert("danger", data.error || "An error occurred");
          btn.disabled = false;
          btn.innerHTML = "Approve Rating";
        }
      })
      .catch((error) => {
        this.showAlert("danger", "Error: " + error.message);
        btn.disabled = false;
        btn.innerHTML = "Approve Rating";
      });
  }

  reject(event) {
    event.preventDefault();
    const btn = event.currentTarget;
    const approvedComps = parseInt(btn.dataset.approvedComps);
    const comments = this.rejectionCommentsTarget.value;

    if (!comments.trim()) {
      alert("Rejection comments are required");
      return;
    }

    let message = "Are you sure you want to reject this rating?";
    if (approvedComps > 0) {
      message += `\n\nWARNING: This rating has ${approvedComps} approved competency check(s). These will also be rejected.`;
    }

    if (!confirm(message)) return;

    btn.disabled = true;
    btn.innerHTML =
      '<i class="glyphicon glyphicon-refresh spinning"></i> Rejecting...';

    fetch(btn.dataset.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: JSON.stringify({ comments: comments }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.showBriefAlert(
            "warning",
            '<i class="glyphicon glyphicon-remove"></i> ' + data.message
          );
          // Redirect to the approvals list
          setTimeout(
            () =>
              (window.location.href =
                btn.dataset.redirectUrl || "/admin/student_rater_approvals"),
            2000
          );
        } else {
          this.showAlert("danger", data.error || "An error occurred");
          btn.disabled = false;
          btn.innerHTML = "Reject Rating";
        }
      })
      .catch((error) => {
        this.showAlert("danger", "Error: " + error.message);
        btn.disabled = false;
        btn.innerHTML = "Reject Rating";
      });
  }

  destroy(event) {
    event.preventDefault();
    const btn = event.currentTarget;
    const totalComps = parseInt(btn.dataset.totalComps);

    let message = "Are you SURE you want to permanently delete this rating?";

    if (totalComps > 0) {
      message += `\n\nWARNING: This will also permanently delete ${totalComps} associated competency check${
        totalComps === 1 ? "" : "s"
      }.`;
    }

    message +=
      "\n\nThis action CANNOT be undone and the student rater will NOT be notified.";

    if (!confirm(message)) return;

    btn.disabled = true;
    btn.innerHTML =
      '<i class="glyphicon glyphicon-refresh spinning"></i> Deleting...';

    fetch(btn.dataset.url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.showBriefAlert(
            "success",
            '<i class="glyphicon glyphicon-ok"></i> ' + data.message
          );
          setTimeout(
            () => (window.location.href = btn.dataset.redirectUrl),
            1500
          );
        } else {
          this.showAlert("danger", data.error || "Error deleting rating");
          btn.disabled = false;
          btn.innerHTML = "Delete Rating Permanently";
        }
      })
      .catch((error) => {
        this.showAlert("danger", "Error: " + error.message);
        btn.disabled = false;
        btn.innerHTML = "Delete Rating Permanently";
      });
  }

  showAlert(type, message) {
    const alertDiv = document.createElement("div");
    alertDiv.className = `alert alert-${type} alert-dismissible`;
    alertDiv.innerHTML = `
      <button type="button" class="close" data-dismiss="alert">&times;</button>
      ${message}
    `;

    const panelBody = this.element.querySelector(".panel-body");
    panelBody.insertBefore(alertDiv, panelBody.firstChild);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  showBriefAlert(type, message) {
    const alertDiv = document.createElement("div");
    alertDiv.className = `alert alert-${type}`;
    alertDiv.style.cssText =
      "position: fixed; top: 20px; right: 20px; z-index: 9999; min-width: 250px; animation: slideIn 0.3s ease-out;";
    alertDiv.innerHTML = message;

    document.body.appendChild(alertDiv);

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      alertDiv.style.animation = "slideOut 0.3s ease-out";
      setTimeout(() => alertDiv.remove(), 300);
    }, 3000);
  }

  updatePendingCount() {
    // Count how many rows still have the 'info' class (pending)
    const table = this.element.querySelector(".panel-info table");

    if (!table) {
      return;
    }

    const pendingRows = table.querySelectorAll("tr.info");
    const pendingCount = pendingRows.length;

    // Update the data-pending-count on the approve button
    if (this.hasApproveBtnTarget) {
      this.approveBtnTarget.dataset.pendingCount = pendingCount;
    }
  }

  csrfToken() {
    return document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute("content");
  }
}
